// The API origin comes from the single source of truth in src/config/api.
// (dev/run → local backend, production/push → prod). Never hardcode the host
// here — change it in src/config/api.ts.
import { API_BASE_URL } from "../config/api";

const BASE_URL = API_BASE_URL;

// Exposed so other modules (e.g. video-entity image URLs in <img src>)
// can build absolute paths against the same origin the apiRequest helper
// uses. Resolves the prod-default / local-override correctly.
export function getApiBaseUrl(): string {
  return BASE_URL;
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  token?: string;
};

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, token } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  // TEMP debug — surfaces in Metro stdout so we can see what URL is being
  // hit and what comes back, without needing Flipper/devtools.
  // eslint-disable-next-line no-console
  console.log(`[api] ${method} ${BASE_URL}${path}`);

  // Retry up to 3 times on 429 Too Many Requests with exponential backoff.
  const MAX_RETRIES = 3;
  let attempt = 0;
  let response: Response;

  while (true) {
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      // eslint-disable-next-line no-console
      console.log(`[api] ← ${response.status} ${path}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(`[api] ✖ ${path} threw:`, e instanceof Error ? e.message : e);
      throw e;
    }

    if (response.status === 429 && attempt < MAX_RETRIES) {
      const delay = 1000 * 2 ** attempt; // 1s, 2s, 4s
      // eslint-disable-next-line no-console
      console.log(`[api] 429 on ${path} — retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
      await new Promise((r) => setTimeout(r, delay));
      attempt++;
      continue;
    }

    break;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let message = text;
    try {
      const json = JSON.parse(text);
      message = json.message ?? text;
    } catch {}
    throw new ApiError(response.status, message);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
